import React, { FC, useEffect, useRef, useState } from 'react'
import AppPageComponent from '@components/page/appPage.component'
import PageHeaderComponent from '@components/page/pageHeader.component'
import PageBodyComponent from '@components/page/pageBody.component'
import {
  Button,
  Card,
  Col,
  FormControl,
  InputGroup,
  ListGroup,
  ListGroupItem,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap'
import {
  BsFillTrashFill,
  BsInfoCircle,
  BsPencil,
  BsPlusCircleFill,
} from 'react-icons/bs'
import AudioEditorComponent from '@components/audio/audioEditor.component'
import { DjangoFilter, SortOrder } from '@store/slices/generic.slice'
import { useTheme } from '@config/theme.config'
import {
  AudioMessageModel,
  clearAllAudioMessages,
  deleteAudioMessage,
  fetchAllAudioMessages,
  selectAllAudioMessages,
  selectAudioMessageById,
  selectAudioNewsLoading,
} from '@store/slices/audioMessage.slice'
import {
  areDatesEqual,
  formatDate,
  formatDjangoTimeString,
  formatSeconds,
} from '../tools/time.tools'
import { getSelectButtonClass } from '../tools/style.tools'
import ReactSelect from 'react-select'
import { reactSelectDarkmode } from '@config/slate.config'
import InfiniteScroll from 'react-infinite-scroll-component'
import { useDispatch, useSelector } from 'react-redux'
import {
  AudioModel,
  fetchAudioById,
  selectAllAudios,
  selectAudioById,
  selectIsAudioIdInProgress,
} from '@store/slices/audio.slice'
import {
  selectAllAuthors,
  selectAuthorsLoading,
} from '@store/slices/author.slice'
import { NewsModel } from '@store/slices/news.slice'
import { AppDispatch, RootState } from '@store/store'
import AudioPlayButton from '@components/shared/audioPlayButtonComponent'
import { EntityId, unwrapResult } from '@reduxjs/toolkit'
import { fetchAllAuthors } from '@store/thunk/author.thunk'
import { toast } from 'react-toastify'

const AudioPage: React.FC = () => {
  const { theme } = useTheme()
  const dispatch = useDispatch<AppDispatch>()
  const audioMessages = useSelector(selectAllAudioMessages)
  const authors = useSelector(selectAllAuthors)
  const isAuthorsLoading = useSelector(selectAuthorsLoading)
  const [search, setSearch] = useState('')
  const [authorFilter, setAuthorFilter] = useState<string | null>(null)
  const [dateFrom, setDateFrom] = useState(formatDate(new Date()))
  const [dateTo, setDateTo] = useState(formatDate(new Date()))
  const [sorting, setSorting] = useState(SortOrder.DESCENDING)
  const [showModal, setShowModal] = useState(false)
  const [page, setPage] = useState(0)
  const [editAudio, setEditAudio] = useState<AudioMessageModel | undefined>(
    undefined
  )
  const [audioMessagesRefreshed, setAudioMessagesRefreshed] = useState<boolean>(false)
  useEffect(() => {
    if (!isAuthorsLoading && authors.length === 0) dispatch(fetchAllAuthors())
  }, [])

  useEffect(() => {
    fetchAudioMessages()
  }, [dispatch, page, search, sorting, dateFrom, dateTo])

  const fetchAudioMessages = () => {
    const filter = new DjangoFilter<NewsModel>()

    // Set limit and offset for Pagination
    const itemsPerPage = 7
    filter.setLimit(itemsPerPage)
    filter.setOffset(itemsPerPage * page)

    // Set Sort Order
    filter.setSortOrder(sorting)

    // Add Sort Field(s)
    filter.addSortField('updated_at')

    filter.setSearch(search)

    // Add any other required filters
    filter.addFilter({
      authorId: authorFilter ? authorFilter : undefined,
    })

    ///TODO: Uncomment when logic ready
    // filter.addDateFilter('updated_at', new Date(dateFrom), new Date(dateTo))

    // Dispatch the fetchAllNews method with the filter
    dispatch(fetchAllAudioMessages(filter))
        .then(() => setAudioMessagesRefreshed(true))
  }

  const handleFetchParamsChange = () => {
    dispatch(clearAllAudioMessages({}))
    setPage(0)
  }

  const handleSearchChange = (searchValue: string) => {
    handleFetchParamsChange()
    // @ts-ignore
    setSearch(searchValue)
  }

  const handleAuthorChange = (searchValue: string | null) => {
    handleFetchParamsChange()
    // @ts-ignore
    setAuthorFilter(searchValue)
  }

  const handleSortFilterChange = (value: SortOrder) => {
    handleFetchParamsChange()
    setSorting(value)
  }

  const dateToFormattedString = (date: Date) =>
    `${date.getDay()}/${date.getMonth()}/${date.getFullYear()}`

  const setDateToAndFrom = (date: Date) => {
    setDateTo(formatDate(date))
    setDateFrom(formatDate(date))
  }

  const onAddAudio = () => {
    setEditAudio(undefined)
    setShowModal(true)
  }
  const handleClose = () => {
    handleFetchParamsChange()
    setShowModal(false)
    fetchAudioMessages()
  }

  const onEditAudio = (audio: AudioMessageModel) => {
    setEditAudio(audio)
    setShowModal(true)
  }

  const onDeleteAudio = (audioToDelete: AudioMessageModel) => {
    dispatch(deleteAudioMessage(audioToDelete.id))
      .then(unwrapResult)
      .then(() => toast.success('Zpráva smazána'))
      .catch((e) => console.error(e))
  }

  const fetchMoreData = () => {
    setPage((page) => page + 1)
  }

  const TodayButton = () => {
    const isToday = areDatesEqual(new Date(), [dateFrom, dateTo])
    return (
      <InputGroup className="d-flex flex-nowrap text-nowrap">
        <Button
          disabled={isToday}
          variant={'outline-secondary'}
          className={getSelectButtonClass(isToday, theme)}
          onClick={() => setDateToAndFrom(new Date())}
        >
          Dnes
        </Button>
      </InputGroup>
    )
  }

  const TomorrowButton = () => {
    const getTomorrowDate = (): Date => {
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      return tomorrow
    }

    const isTomorrow = areDatesEqual(getTomorrowDate(), [dateFrom, dateTo])
    return (
      <InputGroup className="d-flex flex-nowrap text-nowrap">
        <Button
          disabled={isTomorrow}
          variant={'outline-secondary'}
          className={getSelectButtonClass(isTomorrow, theme)}
          onClick={() => setDateToAndFrom(getTomorrowDate())}
        >
          Zítra
        </Button>
      </InputGroup>
    )
  }
  const TimeFrom = () => (
    <InputGroup>
      <FormControl
        type="date"
        value={dateFrom}
        onChange={(e) => setDateFrom(e.target.value)}
      ></FormControl>
    </InputGroup>
  )
  const TimeTo = () => (
    <InputGroup>
      <FormControl
        type="date"
        value={dateTo}
        onChange={(e) => setDateTo(e.target.value)}
      ></FormControl>
    </InputGroup>
  )
  const AuthorFilter = () => {
    const authorOptions = authors.map((author) => ({
      value: author.username,
      label: author.username,
    }))

    return (
      <ReactSelect
        defaultValue={
          authorFilter !== null
            ? authorOptions.find((option) => option.value === authorFilter)
            : null
        }
        placeholder={'Author'}
        options={authorOptions}
        isClearable={true}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) => handleAuthorChange(option ? option.value : null)}
      />
    )
  }

  const Sort = () => {
    const sortingOptions = [
      {
        value: SortOrder.DESCENDING,
        label: 'Od nejnovější',
      },
      {
        value: SortOrder.ASCENDING,
        label: 'Od nejstarší',
      },
    ]

    return (
      <ReactSelect
        defaultValue={
          sorting !== null
            ? sortingOptions.find((option) => option.value === sorting)
            : null
        }
        options={sortingOptions}
        styles={reactSelectDarkmode(theme)}
        onChange={(option) =>
          handleSortFilterChange(option ? option.value : SortOrder.DESCENDING)
        }
      />
    )
  }
  const Search = () => (
    <InputGroup className="">
      <FormControl
        placeholder="Vyhledávání podle názvu nebo ID"
        onChange={(e) => handleSearchChange(e.target.value as string)}
      />
    </InputGroup>
  )
  const AudioRow: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <ListGroupItem style={{ height: '100px' }} className="py-3">
      <Row className="h-100">
        <Col
          className="d-flex align-items-center fw-bold text-secondary"
          style={{ maxWidth: '80px' }}
        >
          {audioMessage.id}
        </Col>
        <Col
          className="my-auto"
          style={{
            minWidth: '250px',
          }}
        >
          <AudioTextField audioMessage={audioMessage}></AudioTextField>
        </Col>
        <Col className="text-secondary d-flex align-items-center">
          <AudioField audioMessageId={audioMessage.id}></AudioField>
        </Col>
        <Col className="text-secondary d-flex align-items-center">
          <DateField audioMessage={audioMessage}></DateField>
        </Col>
        <Col
          className="text-secondary d-flex align-items-center"
          style={{ maxWidth: '150px' }}
        >
          <AuthorField audioMessage={audioMessage}></AuthorField>
        </Col>
        <Col className="text-secondary d-flex align-items-center">
          <UsedField audioMessage={audioMessage}></UsedField>
        </Col>
        <Col
          className="text-secondary d-flex align-items-center"
          style={{ minWidth: '200px' }}
        >
          <NoteField audioMessage={audioMessage}></NoteField>
        </Col>
        <Col
          className="d-flex align-items-center justify-content-end"
          style={{ minWidth: '180px' }}
        >
          <ActionButtons audioMessage={audioMessage}></ActionButtons>
        </Col>
      </Row>
    </ListGroupItem>
  )

  const DateField: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <span className={' w-100'}>
      {dateToFormattedString(new Date(audioMessage.updatedAt))}
    </span>
  )

  const AudioTextField: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <div>
      <p className="p-0 m-0 fw-bold">{audioMessage.name}</p>
      <span>{audioMessage.beginningWords}</span>
      {'...'}
      <span>{audioMessage.endingWords}</span>
    </div>
  )

  const UsedField: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <span className={' w-100'}>
      {audioMessage.used.length > 0
        ? audioMessage.used.join(', ')
        : 'Není použito'}
    </span>
  )

  const AudioField: FC<{ audioMessageId: EntityId }> = ({ audioMessageId }) => {
    const audioMessage: AudioMessageModel = useSelector((state: RootState) =>
      selectAudioMessageById(state, audioMessageId)
    )
    const audio: AudioModel | null = useSelector((state: RootState) =>
      selectAudioById(state, audioMessage?.audioId)
    )
    const audioLoading: boolean = useSelector((state: RootState) =>
      selectIsAudioIdInProgress(state, audioMessage?.audioId)
    )

    useEffect(() => {
      if (audioMessage && !audio && !audioLoading)
        dispatch(fetchAudioById(audioMessage.audioId))
    }, [audioMessage, audio, audioLoading])

    return (
      <div className={'d-flex align-items-center gap-1'}>
        <AudioPlayButton
          title={'Přehrát zprávu'}
          audioId={audio?.id || null}
          showSlider={false}
        ></AudioPlayButton>
        {audio && formatSeconds(formatDjangoTimeString(audio.length))}
      </div>
    )
  }
  const NoteField: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <>
      <BsInfoCircle
        style={{ width: '16px', height: '16px' }}
        className="me-2"
      ></BsInfoCircle>
      <OverlayTrigger
        placement="top"
        delay={{ show: 250, hide: 400 }}
        overlay={renderTooltip(audioMessage.note)}
      >
        <span
          style={{ textOverflow: 'ellipsis', width: '200px' }}
          className="text-nowrap overflow-hidden"
        >
          {audioMessage.note}
        </span>
      </OverlayTrigger>
    </>
  )

  const AuthorField: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <span
      style={{ textOverflow: 'ellipsis', width: '200px' }}
      className="text-nowrap overflow-hidden"
    >
      {audioMessage.updatedBy}
    </span>
  )

  const renderTooltip = (text: string) => <Tooltip>{text}</Tooltip>

  const ActionButtons: FC<{ audioMessage: AudioMessageModel }> = ({
    audioMessage,
  }) => (
    <>
      <Button
        variant="primary"
        className="fw-bold me-2 d-flex align-items-center"
        onClick={() => onEditAudio(audioMessage)}
      >
        <BsPencil className="me-1"></BsPencil> Upravit
      </Button>
      <Button
        variant="outline-danger"
        className="fw-bold"
        onClick={() => onDeleteAudio(audioMessage)}
      >
        <BsFillTrashFill></BsFillTrashFill>
      </Button>
    </>
  )

  return (
    <>
      <AppPageComponent>
        <PageHeaderComponent>
          <div className="pt-4 pb-2">
            <Row className="d-flex px-0 pb-2 w-50">
              <Col className="d-flex pe-4">
                <Col className="pe-2">
                  <AuthorFilter></AuthorFilter>
                </Col>
                <Col>
                  <Sort></Sort>
                </Col>
              </Col>
            </Row>
            <Row className="px-0 w-100 justify-content-start">
              <Col className="px-0" style={{marginLeft: '10px', maxWidth: '136px'}}>
                <Button
                    variant="success"
                    className="fw-bold d-flex align-items-center"
                    onClick={onAddAudio}
                >
                  <BsPlusCircleFill className="me-2"></BsPlusCircleFill> Nové audio
                </Button>
              </Col>
              <Col className="">
                <AudioMessageSearch
                  onSearch={handleSearchChange}
                ></AudioMessageSearch>
              </Col>
              {/*TODO: Uncomment when logic ready*/}
              {/*<Col className="d-flex px-0">*/}
              {/*  <Col className="d-flex flex-grow-0 gap-1 me-2">*/}
              {/*    <TodayButton></TodayButton>*/}
              {/*    <TomorrowButton></TomorrowButton>*/}
              {/*  </Col>*/}
              {/*  <Col>*/}
              {/*    <TimeFrom></TimeFrom>*/}
              {/*  </Col>*/}
              {/*  <Col className="ms-2">*/}
              {/*    <TimeTo></TimeTo>*/}
              {/*  </Col>*/}
              {/*</Col>*/}
            </Row>
          </div>
        </PageHeaderComponent>
        <PageBodyComponent>
          <Card className="mt-1 mb-5">
            <Card.Header>
              <Row>
                <Col style={{ maxWidth: '80px' }}>ID</Col>
                <Col style={{ minWidth: '250px' }}>Název</Col>
                <Col style={{ maxWidth: '130px' }}>Délka</Col>
                <Col style={{ maxWidth: '130px' }}>Datum</Col>
                <Col style={{ maxWidth: '155px' }}>Vytvořil</Col>
                <Col style={{ maxWidth: '135px' }}>Použito</Col>
                <Col style={{ minWidth: '200px' }}>Poznámka</Col>
                <Col style={{ minWidth: '150px' }}></Col>
              </Row>
            </Card.Header>
            <Card.Body className="m-0 p-0">
              <ListGroup variant="flush">
                { audioMessagesRefreshed &&
                  <InfiniteScroll
                      dataLength={audioMessages.length} // length of the data
                      hasMore={true} // indicates if there's more data to load
                      next={fetchMoreData}
                      loader={
                        <h4 className="mx-auto mt-5 w-25 text-center text-secondary">
                          Načítání...
                        </h4>
                      } // loader to show at the end of the list
                      height={650}
                  >
                    {audioMessages.map((audioMessage) => (
                        <AudioRow
                            key={audioMessage.id}
                            audioMessage={audioMessage}
                        ></AudioRow>
                    ))}
                  </InfiniteScroll>
                }
              </ListGroup>
            </Card.Body>
          </Card>
        </PageBodyComponent>
      </AppPageComponent>
      {showModal && (
        <AudioEditorComponent
          audioMessageId={editAudio?.id}
          show={showModal}
          onHide={handleClose}
        ></AudioEditorComponent>
      )}
    </>
  )
}

const AudioMessageSearch: FC<{ onSearch: (search: string) => void }> = ({
  onSearch,
}) => {
  const [search, setSearch] = useState('')
  const searchInputRef = useRef(null)

  useEffect(() => {
    //@ts-ignore
    searchInputRef.current?.focus()
  }, [search])

  const handleSearchChange = (e: React.ChangeEvent) => {
    e.preventDefault()
    //@ts-ignore
    const searchValue = e.target.value
    setSearch(searchValue)
    onSearch(searchValue)
  }

  return (
    <InputGroup className="">
      <FormControl
        placeholder="Vyhledávaní podle klíčového slova nebo ID"
        ref={searchInputRef}
        onChange={handleSearchChange}
      />
    </InputGroup>
  )
}

export default AudioPage
